import { useEffect, type RefObject } from "react";

/** Elements that fade up when they scroll into view on the desktop site. */
const REVEAL = ".dpc:not(.dpc--sk), .dh-tile, .dh-post, .dh-review, .dh-sec h2, .dh-eq, .dh-b2b, .dac-card, .dao-row, .dbl-card, .da-sec";

const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/**
 * Desktop-only motion: page fade-in on every navigation, cards revealed on
 * scroll (staggered), header shadow once the page is scrolled. Everything is
 * skipped for visitors who turned animations off in their system.
 */
export function useDesktopMotion(on: boolean, page: RefObject<HTMLElement | null>, routeKey: string) {
  // Page enter
  useEffect(() => {
    const el = page.current;
    if (!on || !el || reduced()) return;
    el.classList.remove("dk-enter");
    void el.offsetWidth;
    el.classList.add("dk-enter");
    const done = () => el.classList.remove("dk-enter");
    el.addEventListener("animationend", done, { once: true });
    return () => el.removeEventListener("animationend", done);
  }, [on, routeKey, page]);

  // Reveal on scroll
  useEffect(() => {
    const root = page.current;
    if (!on || !root || reduced() || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const el = e.target as HTMLElement;
          io.unobserve(el);
          el.classList.add("dk-in");
          window.setTimeout(() => {
            el.classList.remove("dk-rv", "dk-in");
            el.style.removeProperty("--rv-i");
          }, 1100);
        }
      },
      { rootMargin: "0px 0px -6% 0px", threshold: 0.08 },
    );
    const scan = () => {
      root.querySelectorAll<HTMLElement>(REVEAL).forEach((el) => {
        if (el.dataset.rv) return;
        el.dataset.rv = "1";
        const siblings = el.parentElement ? Array.from(el.parentElement.children) : [];
        el.style.setProperty("--rv-i", String(Math.min(6, Math.max(0, siblings.indexOf(el)))));
        el.classList.add("dk-rv");
        io.observe(el);
      });
    };
    scan();
    const mo = new MutationObserver(scan);
    mo.observe(root, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, [on, page]);

  // Header shadow
  useEffect(() => {
    if (!on) return;
    const header = document.querySelector(".dk-header");
    const onScroll = () => header?.classList.toggle("dk-header--scrolled", window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [on]);
}
